<template>
  <v-col cols="12" v-if="permisoVista('sensores', 'r')" :class="{ 'embedded-col': embedded }">
    <v-card class="sensor-config-card" :class="{ 'sensor-config-card--embedded': embedded }" elevation="0">
      <div v-if="!embedded" class="sensor-config-card__glow"></div>

      <div v-if="!embedded" class="sensor-config-card__header">
        <div>
          <div class="sensor-config-card__eyebrow">Configuracion</div>
          <h2 class="sensor-config-card__title">Variables</h2>
          <p class="sensor-config-card__subtitle">
            Administra las variables y la unidad con la que reportan.
          </p>
        </div>

        <v-btn
          v-if="permisoVista('sensores', 'c')"
          color="primary"
          dark
          class="sensor-config-card__cta"
          @click="openCreateExternal"
        >
          <v-icon left>mdi-plus-circle-outline</v-icon>
          Nueva Variable
        </v-btn>
      </div>

      <v-dialog v-model="dialog" persistent max-width="520px">
        <v-card class="sensor-dialog">
            <v-card-title class="sensor-dialog__title">
              {{ formTitle }}
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model.trim="editedItem.codigo"
                      label="Codigo"
                      prepend-inner-icon="mdi-pound"
                      outlined
                      dense
                      autofocus
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model.trim="editedItem.nombre"
                      label="Nombre de la Variable"
                      prepend-inner-icon="mdi-variable"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-autocomplete
                      v-model="editedItem.idUnidad"
                      :items="unidades"
                      :item-text="unidadLabel"
                      item-value="idUnidad"
                      label="Unidad"
                      prepend-inner-icon="mdi-scale-balance"
                      :loading="loadingUnidades"
                      :disabled="loadingUnidades"
                      outlined
                      dense
                      clearable
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model.trim="editedItem.descripcion"
                      label="Descripcion"
                      prepend-inner-icon="mdi-text-box-outline"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-alert
                  v-if="errores.length > 0"
                  type="error"
                  text
                  dense
                  class="mb-0"
                >
                  <div
                    v-for="(error, index) in errores"
                    :key="'error-variable-' + index"
                  >
                    {{ error }}
                  </div>
                </v-alert>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" text @click="close">
                Cancelar
              </v-btn>
              <v-btn
                color="red darken-1"
                text
                :disabled="saving"
                :loading="saving"
                @click="save"
              >
                Guardar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

      <div class="sensor-config-card__toolbar">
        <v-text-field
          v-model="search"
          append-icon="search"
          label="Buscar variable, codigo o unidad"
          single-line
          hide-details
          outlined
          dense
          class="sensor-config-card__search"
        ></v-text-field>

        <v-btn text color="primary" @click="initialize">
          <v-icon left small>mdi-refresh</v-icon>
          Refrescar
        </v-btn>
      </div>

      <v-dialog v-model="dialogDependencias" max-width="520px">
        <v-card v-if="dependItem" class="depend-dialog">
          <v-card-title class="depend-dialog__title">
            <v-icon color="warning" left>mdi-alert-outline</v-icon>
            No se puede desactivar la variable
          </v-card-title>
          <v-card-text>
            <p class="mb-3">
              La variable <strong>{{ dependItem.codigo }}</strong> no puede desactivarse porque tiene
              <strong>{{ dependItem.relacionesActivas }} relación(es) activa(s)</strong> con sensores.
            </p>
            <div class="subtitle-2 mb-1">Sensores asociados:</div>
            <ul class="depend-dialog__list">
              <li v-for="(s, i) in dependItem.relacionesActivasLista" :key="i">{{ s }}</li>
            </ul>
            <v-alert dense text type="warning" class="mb-0 mt-3">
              Para desactivar esta variable, primero desasocia o cambia la variable en esos sensores.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="dialogDependencias = false">Cerrar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-data-table
        class="sensor-config-table"
        :headers="headers"
        :items="variables"
        :search="search"
        :loading="loading"
        loading-text="Cargando... Espere, Por Favor."
      >
        <template v-slot:[`item.nombre`]="{ item }">
          <div class="sensor-name-cell">
            <div class="sensor-name-cell__icon">
              <v-icon color="primary" small>mdi-variable</v-icon>
            </div>
            <div>
              <div class="sensor-name-cell__title">{{ item.nombre }}</div>
              <div class="sensor-name-cell__caption">
                Codigo: {{ item.codigo }}
              </div>
            </div>
          </div>
        </template>

        <template v-slot:[`item.unidad`]="{ item }">
          <v-chip
            small
            outlined
            color="primary"
            class="sensor-unit-chip"
          >
            {{ item.unidadLabel || "N/A" }}
          </v-chip>
        </template>

        <template v-slot:[`item.descripcion`]="{ item }">
          <span class="text--secondary">{{ item.descripcion || "N/A" }}</span>
        </template>

        <template v-slot:[`item.relacionesActivas`]="{ item }">
          <div class="asoc-cell">
            <span class="asoc-cell__count">
              {{ item.relacionesActivas || 0 }} relación{{ item.relacionesActivas === 1 ? '' : 'es' }}
            </span>
            <a v-if="item.relacionesActivas > 0" class="asoc-cell__link" @click="verAsociaciones(item)">Ver detalle</a>
            <span v-else class="asoc-cell__empty">Sin relaciones</span>
          </div>
        </template>

        <template v-slot:[`item.condicion`]="{ item }">
          <v-switch
            :key="'var-sw-' + item.idVariable + '-' + estadoNonce"
            :input-value="item.condicion !== false"
            :loading="togglingId === item.idVariable"
            :disabled="togglingId === item.idVariable || !(permisoVista('sensores','u') || permisoVista('sensores','d'))"
            color="success"
            dense
            hide-details
            inset
            class="estado-switch"
            :label="item.condicion === false ? 'Inactiva' : 'Activa'"
            @change="toggleEstado(item)"
          ></v-switch>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip
            v-if="permisoVista('sensores', 'u')"
            bottom
            color="black"
            class="white--text"
          >
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                icon
                small
                color="blue darken-1"
                @click="editItem(item)"
              >
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <span class="white--text">Editar</span>
          </v-tooltip>
        </template>

        <template v-slot:no-data>
          <div class="sensor-empty-state">
            <v-icon color="blue-grey lighten-1" large>
              mdi-variable-box
            </v-icon>
            <div class="sensor-empty-state__title">No hay variables registradas</div>
            <div class="sensor-empty-state__subtitle">
              Crea una variable nueva para comenzar.
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-snackbar
      v-model="snackbar.show"
      top
      centered
      :timeout="3200"
      :color="snackbar.color"
      class="sensor-snackbar"
    >
      <div class="sensor-snackbar__content">
        <v-icon dark left>{{ snackbar.icon }}</v-icon>
        <span>{{ snackbar.text }}</span>
      </div>
    </v-snackbar>
  </v-col>
</template>

<script>
import axios from "axios";

export default {
  props: {
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      unidades: [],
      loading: false,
      loadingUnidades: false,
      saving: false,
      dialog: false,
      dialogDependencias: false,
      dependItem: null,
      togglingId: null,
      estadoNonce: 0,
      search: "",
      variables: [],
      errores: [],
      editedIndex: -1,
      headers: [
        {
          text: "Variable",
          value: "nombre",
          align: "left",
          sortable: true,
        },
        {
          text: "Unidad",
          value: "unidad",
          align: "left",
          sortable: true,
        },
        {
          text: "Descripcion",
          value: "descripcion",
          align: "left",
          sortable: true,
        },
        {
          text: "Relaciones",
          value: "relacionesActivas",
          align: "center",
          sortable: true,
        },
        {
          text: "Estado",
          value: "condicion",
          align: "center",
          sortable: true,
        },
        {
          text: "Acciones",
          value: "actions",
          sortable: false,
          align: "center",
          width: "12%",
        },
      ],
      editedItem: {
        idVariable: null,
        codigo: "",
        nombre: "",
        idUnidad: null,
        descripcion: "",
      },
      defaultItem: {
        idVariable: null,
        codigo: "",
        nombre: "",
        idUnidad: null,
        descripcion: "",
      },
      snackbar: {
        show: false,
        text: "",
        color: "success",
        icon: "mdi-check-circle-outline",
      },
    };
  },
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "Crear Variable" : "Editar Variable";
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.setLoading(true);
      this.getUnidades();
      axios
        .get("/Variables?incluirInactivos=true")
        .then((response) => {
          const items = Array.isArray(response.data) ? response.data : [];
          this.variables = items.map(this.normalizeItem);
          this.setLoading(false);
          this.$emit("count-changed", this.variables.length);
        })
        .catch((error) => {
          this.setLoading(false);
          this.handleError(error, "No se pudieron cargar las variables.");
        });
    },
    setLoading(value) {
      this.loading = value;
      this.$emit("loading-changed", value);
    },
    openCreate() {
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.errores = [];
    },
    openCreateExternal() {
      this.openCreate();
      this.dialog = true;
    },
    unidadLabel(item) {
      if (!item) return "";
      const nombre = item.nombre || item.Nombre || "";
      const abreviatura = item.abreviatura || item.Abreviatura || "";
      if (nombre && abreviatura) return `[${abreviatura}] - ${nombre}`;
      return nombre || abreviatura || "";
    },
    resolveUnidadLabel(item, idUnidad) {
      const unidadObj = item.unidad && typeof item.unidad === "object"
        ? item.unidad
        : item.Unidad && typeof item.Unidad === "object"
          ? item.Unidad
          : null;
      if (unidadObj) return this.unidadLabel(unidadObj);

      const unidadNombre =
        item.unidad ||
        item.Unidad ||
        item.nombreUnidad ||
        item.NombreUnidad ||
        "";
      const abreviaturaUnidad =
        item.unidadAbreviatura ||
        item.UnidadAbreviatura ||
        item.unidadCodigo ||
        item.UnidadCodigo ||
        item.abreviaturaUnidad ||
        item.AbreviaturaUnidad ||
        "";
      if (unidadNombre || abreviaturaUnidad) {
        return this.unidadLabel({
          nombre: unidadNombre,
          abreviatura: abreviaturaUnidad,
        });
      }

      const unidad = this.unidades.find((u) => u.idUnidad === idUnidad);
      return unidad ? this.unidadLabel(unidad) : "";
    },
    normalizeItem(item = {}) {
      const idUnidad =
        item.idUnidad ||
        item.IdUnidad ||
        item.unidadId ||
        item.UnidadId ||
        (item.unidad && (item.unidad.idUnidad || item.unidad.IdUnidad)) ||
        null;

      return {
        ...item,
        idVariable:
          item.idVariable ||
          item.IdVariable ||
          item.id ||
          item.Id ||
          null,
        idUnidad,
        codigo: item.codigo || item.Codigo || "",
        nombre: item.nombre || item.Nombre || "",
        descripcion: item.descripcion || item.Descripcion || "",
        condicion: typeof item.condicion === "boolean" ? item.condicion : item.Condicion,
        relacionesActivas: item.relacionesActivas ?? item.RelacionesActivas ?? 0,
        relacionesActivasLista: item.relacionesActivasLista || item.RelacionesActivasLista || [],
        unidadLabel: this.resolveUnidadLabel(item, idUnidad),
      };
    },
    getUnidades() {
      this.loadingUnidades = true;
      axios
        .get("/Unidades")
        .then((response) => {
          const unidades = Array.isArray(response.data) ? response.data : [];
          const unidadesMap = {};
          for (let i = 0; i < unidades.length; i += 1) {
            const unidad = unidades[i];
            const idUnidad = unidad && (unidad.idUnidad || unidad.IdUnidad);
            if (idUnidad == null) continue;
            if (!unidadesMap[idUnidad]) {
              unidadesMap[idUnidad] = unidad;
            }
          }
          this.unidades = Object.values(unidadesMap);
          this.variables = this.variables.map((item) => ({
            ...item,
            unidadLabel: this.resolveUnidadLabel(item, item.idUnidad),
          }));
          this.loadingUnidades = false;
        })
        .catch((error) => {
          this.loadingUnidades = false;
          console.log(error);
        });
    },
    validate() {
      this.errores = [];
      if (!this.editedItem.codigo) {
        this.errores.push("El codigo es obligatorio.");
      }
      if (this.editedItem.codigo && this.editedItem.codigo.length > 30) {
        this.errores.push("El codigo no puede exceder 30 caracteres.");
      }
      if (!this.editedItem.nombre) {
        this.errores.push("El nombre es obligatorio.");
      }
      if (this.editedItem.nombre && this.editedItem.nombre.length > 120) {
        this.errores.push("El nombre no puede exceder 120 caracteres.");
      }
      if (this.editedItem.descripcion && this.editedItem.descripcion.length > 255) {
        this.errores.push("La descripcion no puede exceder 255 caracteres.");
      }
      if (!this.editedItem.idUnidad) {
        this.errores.push("Seleccione una unidad.");
      }
      return this.errores.length === 0;
    },
    save() {
      if (!this.validate()) return;

      this.saving = true;
      const payload = {
        codigo: this.editedItem.codigo,
        nombre: this.editedItem.nombre,
        descripcion: this.editedItem.descripcion || null,
        idUnidad: this.editedItem.idUnidad,
      };

      const request = this.editedIndex === -1
        ? axios.post("/Variables/Crear", payload)
        : axios.put(`/Variables/Actualizar/${this.editedItem.idVariable}`, payload);

      request
        .then(() => {
          this.saving = false;
          this.close();
          this.initialize();
          this.showSnackbar(
            "Variable guardada correctamente.",
            "success",
            "mdi-check-circle-outline"
          );
        })
        .catch((error) => {
          this.saving = false;
          this.handleError(error, "No se pudo guardar la variable.");
        });
    },
    editItem(item) {
      this.editedIndex = this.variables.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.errores = [];
      this.dialog = true;
    },
    toggleEstado(item) {
      const idVariable = item && item.idVariable;
      if (!idVariable) {
        this.showSnackbar(
          "No se encontro el identificador de la variable.",
          "error",
          "mdi-alert-circle-outline"
        );
        return;
      }

      const activar = item.condicion === false;

      if (!activar && item.relacionesActivas > 0) {
        this.verAsociaciones(item);
        this.estadoNonce += 1;
        return;
      }

      this.togglingId = idVariable;
      axios
        .put(`/Variables/${activar ? "Activar" : "Desactivar"}/${idVariable}`)
        .then(() => {
          this.initialize();
          this.showSnackbar(
            activar ? "Variable activada correctamente." : "Variable desactivada correctamente.",
            "success",
            activar ? "mdi-check-circle-outline" : "mdi-delete-circle-outline"
          );
        })
        .catch((error) => {
          const status = error && error.response ? error.response.status : null;
          if (!activar && status === 409) {
            this.verAsociaciones(item);
          } else {
            this.handleError(
              error,
              activar ? "No se pudo activar la variable." : "No se pudo desactivar la variable."
            );
          }
          this.initialize();
        })
        .finally(() => {
          this.togglingId = null;
          this.estadoNonce += 1;
        });
    },
    verAsociaciones(item) {
      this.dependItem = item;
      this.dialogDependencias = true;
    },
    close() {
      this.dialog = false;
      this.errores = [];
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    showSnackbar(text, color = "error", icon = "mdi-alert-circle-outline") {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.icon = icon;
      this.snackbar.show = true;
    },
    handleError(error, fallbackMessage) {
      const message =
        error &&
        error.response &&
        error.response.data &&
        typeof error.response.data === "string"
          ? error.response.data
          : fallbackMessage;

      if (this.dialog) {
        this.errores = [message];
      }
      this.showSnackbar(message, "error", "mdi-alert-circle-outline");
      console.log(error);
    },
  },
};
</script>

<style scoped>
.sensor-config-card {
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  padding: 24px;
  background:
    radial-gradient(circle at top right, rgba(25, 118, 210, 0.14), transparent 28%),
    linear-gradient(135deg, #ffffff 0%, #f7fafc 100%);
  border: 1px solid rgba(25, 118, 210, 0.10);
}

.sensor-config-card--embedded {
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
}

.embedded-col {
  padding: 0;
}

.sensor-config-card__glow {
  position: absolute;
  inset: auto -60px -80px auto;
  width: 220px;
  height: 220px;
  background: radial-gradient(circle, rgba(25, 118, 210, 0.12), transparent 68%);
  pointer-events: none;
}

.sensor-config-card__header {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  margin-bottom: 18px;
}

.sensor-config-card__eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 12px;
  font-weight: 700;
  color: #1976d2;
  margin-bottom: 6px;
}

.sensor-config-card__title {
  margin: 0;
  font-size: 30px;
  line-height: 1.05;
  color: #102a43;
}

.sensor-config-card__subtitle {
  margin: 8px 0 0;
  color: #5c6b7a;
  max-width: 560px;
}

.sensor-config-card__cta {
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(25, 118, 210, 0.22);
}

.sensor-config-card__toolbar {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.sensor-config-card__search {
  max-width: 380px;
}

.sensor-config-table {
  background: transparent;
}

.sensor-name-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
}

.sensor-name-cell__icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(25, 118, 210, 0.10);
}

.sensor-name-cell__title {
  font-weight: 700;
  color: #102a43;
}

.sensor-name-cell__caption {
  font-size: 12px;
  color: #7b8794;
}

.sensor-unit-chip {
  font-weight: 600;
}

.estado-switch {
  display: inline-flex;
  margin-top: 0;
  padding-top: 0;
}

.asoc-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.25;
}
.asoc-cell__count {
  font-weight: 600;
  color: #334e68;
}
.asoc-cell__link {
  font-size: 12px;
  color: #1976d2;
  cursor: pointer;
}
.asoc-cell__link:hover {
  text-decoration: underline;
}
.asoc-cell__empty {
  font-size: 12px;
  color: #94a3b8;
}
.depend-dialog__title {
  font-weight: 700;
  color: #b45309;
  word-break: normal;
}
.depend-dialog__list {
  margin: 0;
  padding-left: 18px;
}
.depend-dialog__list li {
  margin-bottom: 2px;
}

.sensor-empty-state {
  padding: 30px 16px;
  text-align: center;
}

.sensor-empty-state__title {
  margin-top: 10px;
  font-weight: 700;
  color: #334e68;
}

.sensor-empty-state__subtitle {
  margin-top: 4px;
  color: #7b8794;
}

.sensor-dialog {
  border-radius: 18px;
}

.sensor-dialog__title {
  color: #102a43;
  font-weight: 700;
}

.sensor-snackbar__content {
  min-width: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-weight: 600;
}

@media (max-width: 960px) {
  .sensor-config-card {
    padding: 18px;
  }

  .sensor-config-card__header,
  .sensor-config-card__toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .sensor-config-card__search {
    max-width: none;
  }
}
</style>
